import { useCallback, useEffect, useMemo, useRef } from 'react'

import { Dayjs } from 'dayjs'

import _dayjs from 'src/Helpers/dayjs'
import { useAppDispatch, useAppSelector } from 'src/Hooks/redux'
import { useHasPlan } from 'src/Hooks/useHasPlan'
import { useGetSubscriptionQuery } from 'src/Redux/subscription-process/subscriptionApi'
import { getScheduledTikTokCampaign } from 'src/Redux/schedule-process/tiktokCampaign/selectors'
import { updateTikTokCampaign } from 'src/Redux/schedule-process/tiktokCampaign/api-actions'

type UseTikTokScheduleStartDate = {
  startDate: Dayjs
  todayEnable: boolean
  onChangeDate: (date: Dayjs) => void
}

const getDefaultDate = (hasPlan: boolean): Dayjs =>
  hasPlan ? _dayjs() : _dayjs().add(1, 'day')

export const useTikTokScheduleStartDate = (
  urlCampaignId: number,
): UseTikTokScheduleStartDate => {
  const dispatch = useAppDispatch()
  const scheduledCampaign = useAppSelector(getScheduledTikTokCampaign)

  const hasPlan = useHasPlan()
  const { isLoading: isSubscriptionLoading } = useGetSubscriptionQuery()

  const campaignId = Number(scheduledCampaign.id)
  const { comment, startDate: scheduledStartDate } = scheduledCampaign

  const startDate = useMemo(() => {
    if (scheduledStartDate) {
      return _dayjs(scheduledStartDate)
    }
    return getDefaultDate(hasPlan)
  }, [scheduledStartDate, hasPlan])

  const persistedDefaultCampaignIdRef = useRef<number | null>(null)

  useEffect(() => {
    const isReady =
      !isSubscriptionLoading &&
      campaignId === urlCampaignId &&
      persistedDefaultCampaignIdRef.current !== campaignId

    if (!isReady) {
      return
    }
    persistedDefaultCampaignIdRef.current = campaignId

    const defaultDate = scheduledStartDate
      ? _dayjs(scheduledStartDate)
      : getDefaultDate(hasPlan)

    void dispatch(
      updateTikTokCampaign({
        id: campaignId,
        comment: comment || '',
        startDate: defaultDate.toISOString(),
      }),
    )
  }, [
    campaignId,
    comment,
    dispatch,
    hasPlan,
    isSubscriptionLoading,
    scheduledStartDate,
    urlCampaignId,
  ])

  const onChangeDate = useCallback(
    (date: Dayjs) => {
      if (!campaignId) {
        return
      }
      void dispatch(
        updateTikTokCampaign({
          id: campaignId,
          comment: comment || '',
          startDate: date.toISOString(),
        }),
      )
    },
    [campaignId, comment, dispatch],
  )

  return { startDate, onChangeDate, todayEnable: hasPlan }
}
